import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Outlet, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { Business } from "../types";
import { BusinessSchema, forensicValidate } from "../lib/schemas";
import { LoadingScreen } from "../components/common/LoadingScreen";
import { StatusScreen } from "../components/common/StatusScreen";

/**
 * AppLayout (formerly BusinessLayout)
 * Purpose: Centralized layout for business-specific nodes.
 * Protocol: Identity Handshake & Context Provisioning | Auth Redirector.
 */
export function AppLayout() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const businessRef = useRef<Business | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    businessRef.current = business;
    if (business?.primary_color) {
      document.documentElement.style.setProperty(
        "--primary-color",
        business.primary_color,
      );
    }
  }, [business]);

  const fetchBusiness = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!slug) return;
      const { silent = false } = options || {};

      try {
        // Step 0: Silent Handshake: Only show full loading if we have no business node AND not silent
        if (!businessRef.current && !silent) {
          setLoading(true);
        }
        setError(null);

        // Auth Sync: Ensure token is hydrated for authenticated routes
        if (location.pathname.includes("/admin")) {
          // Step 1: Fast check from local storage/memory
          const {
            data: { session },
          } = await supabase.auth.getSession();
          let user: { id: string } | undefined = session?.user as
            | { id: string }
            | undefined;

          // Step 2: If no session, try definitive check (handles token refresh)
          if (!user) {
            const {
              data: { user: verifiedUser },
              error: authError,
            } = await supabase.auth.getUser();

            if (
              authError &&
              (authError.status === 400 ||
                authError.message?.includes("Invalid Refresh Token"))
            ) {
              console.warn(
                "[Auth] Invalid refresh token detected. Clearing local session.",
              );
              await supabase.auth.signOut({ scope: "local" });
              navigate("/login?error=session_expired", { replace: true });
              return;
            }

            user = verifiedUser ?? undefined;
          }

          // Step 3: Sovereign Protocol: Race Condition Guard
          // If still null, wait briefly to allow client-side hydration to complete
          // Skip this wait if we already have a business node (silent sync)
          if (!user && !businessRef.current) {
            await new Promise((r) => setTimeout(r, 1500));

            // Mounting Guard: Stop if component unmounted during delay
            if (!isMounted.current) return;

            const {
              data: { user: retryUser },
              error: retryError,
            } = await supabase.auth.getUser();

            if (
              retryError &&
              (retryError.status === 400 ||
                retryError.message?.includes("Invalid Refresh Token"))
            ) {
              console.warn(
                "[Auth] Invalid refresh token detected on retry. Clearing local session.",
              );
              await supabase.auth.signOut({ scope: "local" });
              navigate("/login?error=session_expired", { replace: true });
              return;
            }

            user = retryUser ?? undefined;
          }

          if (!user && !businessRef.current) {
            navigate("/login?error=session_expired", { replace: true });
            return;
          }
        }

        const { data, error: fetchError } = await supabase
          .from("businesses")
          .select("*")
          .eq("slug", slug)
          .maybeSingle();

        if (fetchError) throw fetchError;
        // `data` is legitimately null when no business matches the slug --
        // that's the normal "not found" case, not a validation failure.
        const validatedBusiness = data
          ? (forensicValidate(
              BusinessSchema,
              data,
              "Business Node",
            ) as Business)
          : null;
        if (isMounted.current) {
          setBusiness(validatedBusiness);
        }
      } catch (err) {
        console.error("Layout Fetch Error:", err);
        if (isMounted.current) {
          setError("Business Node Not Found");
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    },
    [slug, location.pathname, navigate],
  );

  useEffect(() => {
    // Initial fetch
    fetchBusiness();

    // Reactive Handshake: Listen for auth events that might change visibility/permissions
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Protocol: Only trigger re-fetch on definitive SIGNED_IN events.
      // INITIAL_SESSION is handled by the manual call on Line 72.
      if (session && event === "SIGNED_IN") {
        fetchBusiness({ silent: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchBusiness]);

  if (loading) {
    return <LoadingScreen color={business?.primary_color} />;
  }

  if (error || !business) {
    return (
      <StatusScreen
        variant="not_found"
        title="Business Not Found"
        message="We couldn’t find this business."
        description="The business may have been removed, is temporarily unavailable, or the link may be incorrect"
        actions={[
          {
            label: "Retry Connection",
            onClick: () => fetchBusiness(),
            variant: "primary",
          },
          {
            label: "Go to Home",
            onClick: () => navigate("/"),
            variant: "secondary",
          },
        ]}
      />
    );
  }

  // Precise Route Isolation: Determine if we are on an administrative node
  const pathSegments = location.pathname.split("/").filter(Boolean);
  // Pattern: [slug, 'admin', ...]
  const isAdminPath = pathSegments[1] === "admin";

  if (!business.is_active && !isAdminPath) {
    return (
      <StatusScreen
        variant="restricted"
        title="Booking Suspended"
        message="BUSINESS OFFLINE"
        description="Booking is temporarily unavailable. Please contact the business directly or try again later"
        primaryColor={business.primary_color ?? undefined}
      />
    );
  }

  // Pass the business data to child routes via context
  return <Outlet context={{ business }} />;
}
